import React, { useState, useCallback, useEffect } from 'react';
import { Copy, Mail } from 'lucide-react';
import InlinePopover from '../../components/ui/InlinePopover';
import WhatsAppIcon from './WhatsAppIcon';
import { useToast } from '../../components/ui/Toast';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import usePaymentLinkActions from './usePaymentLinkActions';
import './SendPaymentLinkPopover.css';

/**
 * Sales Tools — Phase 3.3.
 *
 * Anchored popover with three send options for an existing UNPAID order.
 * The first action that fires creates the PaymentLink row + URL; subsequent
 * actions reuse the same row (one token per popover session).
 *
 * Props:
 *   isOpen, onClose, anchorEl  — passed straight to InlinePopover
 *   order                       — { id, customerInfo, ... }
 *   onLinkCreated?              — optional callback once a link exists
 *                                 (used by Order Detail to refresh the
 *                                 sent-history list)
 */
export default function SendPaymentLinkPopover({
  isOpen,
  onClose,
  anchorEl,
  order,
  onLinkCreated,
}) {
  const toast = useToast();
  const { createLink, emailLink } = usePaymentLinkActions();
  const { hasAccess: canEmail } = useFeatureAccess('sales.link_email');

  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  // Reset link state when the popover closes so reopening on a different
  // order doesn't reuse a stale token.
  useEffect(() => {
    if (!isOpen) {
      setLink(null);
      setBusy(false);
    }
  }, [isOpen]);

  // Null-safe id — Orders.js passes order=null after the popover
  // closes (to release the row reference), which used to throw on
  // useCallback dep [order.id] during the re-render.
  const orderId = order?.id || null;

  const ensureLink = useCallback(async () => {
    if (!orderId) return null;
    if (link) return link;
    setBusy(true);
    const fresh = await createLink(orderId);
    setBusy(false);
    if (fresh) {
      setLink(fresh);
      if (typeof onLinkCreated === 'function') onLinkCreated(fresh);
    }
    return fresh;
  }, [link, createLink, orderId, onLinkCreated]);

  const handleCopy = useCallback(async () => {
    const l = await ensureLink();
    if (!l?.url) {
      onClose();
      return;
    }
    try {
      await navigator.clipboard.writeText(l.url);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
    onClose();
  }, [ensureLink, toast, onClose]);

  const customerEmail = order?.customerInfo?.email || null;
  const customerPhone = order?.customerInfo?.phone || null;

  const handleEmail = useCallback(async () => {
    if (!canEmail) {
      toast.info('Email sending is a PRO feature');
      onClose();
      return;
    }
    const l = await ensureLink();
    if (!l) {
      onClose();
      return;
    }
    await emailLink(l.id, customerEmail);
    onClose();
  }, [canEmail, ensureLink, emailLink, customerEmail, toast, onClose]);

  const handleWhatsApp = useCallback(async () => {
    const l = await ensureLink();
    if (!l?.url) {
      onClose();
      return;
    }
    const digits = String(customerPhone || '').replace(/\D+/g, '');
    if (!digits) {
      toast.error('No phone number on this order');
      onClose();
      return;
    }
    const greeting = 'Here is your secure payment link: ';
    window.open(
      `https://wa.me/${digits}?text=${encodeURIComponent(greeting + l.url)}`,
      '_blank',
      'noopener,noreferrer'
    );
    toast.success('Opening WhatsApp…');
    onClose();
  }, [ensureLink, customerPhone, toast, onClose]);

  const hasEmail = !!customerEmail;
  const hasPhone = !!customerPhone;

  // Don't render anything (and don't run the popover's effects) when
  // there's no order context. InlinePopover already short-circuits on
  // !isOpen, but this is an extra belt-and-braces against the brief
  // render after close where order is null.
  if (!order) return null;

  return (
    <InlinePopover isOpen={isOpen} onClose={onClose} anchorEl={anchorEl} width={232}>
      <div className="sl-pl-popover">
        <p className="sl-pl-popover-title">Send payment link</p>

        <button
          type="button"
          className="sl-pl-action sl-pl-action--primary"
          onClick={handleCopy}
          disabled={busy}
        >
          <Copy size={15} />
          <span>Copy URL</span>
        </button>

        <button
          type="button"
          className="sl-pl-action"
          onClick={handleEmail}
          disabled={busy || !hasEmail}
          title={!hasEmail ? 'This order has no customer email' : undefined}
        >
          <Mail size={15} />
          <span>Send by email</span>
          {!canEmail && <span className="sl-pl-action-badge">PRO</span>}
        </button>

        <button
          type="button"
          className="sl-pl-action sl-pl-action--whatsapp"
          onClick={handleWhatsApp}
          disabled={busy || !hasPhone}
          title={!hasPhone ? 'This order has no customer phone' : undefined}
        >
          <WhatsAppIcon size={15} />
          <span>Send by WhatsApp</span>
        </button>
      </div>
    </InlinePopover>
  );
}
